/*
 * Purpose: Shared workflow helpers (labels, persistence, starter workflow)
 * Last modified: 2026-06-25 | Agent
 * Completeness: 95
 */
import type { Node, Workflow } from '@/stores/workflowStore';
import { getBlockTypeLabel } from '@/lib/blockTypes';
import { getContextualDisplayName } from '@/lib/blockContext';
import { workflowsStorageKey, migrateLegacyWorkflowsIfNeeded } from '@/lib/tenantStorage';
import { createMissionStartNode, createMissionGoalNode } from '@/lib/missionBlocks';
import { normalizeWorkflowMissionGraph, workflowNeedsMissionAnchors } from '@/lib/missionAnchors';

export function getNodeLabel(node: Node): string {
  const typeFallback = getBlockTypeLabel(node.type, node.subtype);

  if (node.type === 'goal' && node.subtype === 'outcome') {
    const outcome = String(node.config?.outcome ?? '').trim();
    if (outcome) {
      return outcome.length > 48 ? `${outcome.slice(0, 47)}…` : outcome;
    }
  }

  return getContextualDisplayName(node.config ?? {}, typeFallback);
}

export function persistWorkflows(workflows: Workflow[]): void {
  migrateLegacyWorkflowsIfNeeded();
  localStorage.setItem(workflowsStorageKey(), JSON.stringify(workflows));
}

export function parseWorkflowDates(workflow: Workflow): Workflow {
  return {
    ...workflow,
    createdAt: new Date(workflow.createdAt),
    updatedAt: new Date(workflow.updatedAt),
  };
}

const genWorkflowId = () => Math.random().toString(36).slice(2, 11);

/** Runnable blank flow — START, GOAL, manual trigger, and log action wired end-to-end. */
export function createRunnableBlankWorkflow(
  name: string,
  id = genWorkflowId()
): Workflow {
  const start = createMissionStartNode(80, 80);
  const goal = createMissionGoalNode(`Accomplish: ${name}`, 300, 80);
  const trigger = {
    id: genWorkflowId(),
    type: 'trigger' as const,
    subtype: 'manual' as const,
    config: { name: 'Manual Trigger' },
    x: 520,
    y: 120,
  };
  const action = {
    id: genWorkflowId(),
    type: 'action' as const,
    subtype: 'log' as const,
    config: { name: 'Log Result', message: `${name} — completed successfully` },
    x: 740,
    y: 120,
  };

  return {
    id,
    name,
    nodes: [start, goal, trigger, action],
    edges: [
      { id: genWorkflowId(), from: start.id, to: goal.id },
      { id: genWorkflowId(), from: goal.id, to: trigger.id },
      { id: genWorkflowId(), from: trigger.id, to: action.id },
    ],
    createdAt: new Date(),
    updatedAt: new Date(),
    missionMeta: {
      allowDynamicMap: true,
      mapLocked: false,
      flowOrientation: 'auto',
      flowTracingMode: false,
      smartAutoConfigureOnConnect: true,
      smartAutoFixPolicy: 'safe-and-review',
      smartInputModalBehavior: 'badge-only',
      truthFactorStrictness: 'balanced',
    },
  };
}

export function createStarterWorkflow(): Workflow {
  return createRunnableBlankWorkflow('Quick Start Workflow', 'starter-quick-start');
}

export function loadWorkflowsFromStorage(): Workflow[] {
  migrateLegacyWorkflowsIfNeeded();
  const stored = localStorage.getItem(workflowsStorageKey());
  if (stored) {
    try {
      const parsed = JSON.parse(stored) as Workflow[];
      if (Array.isArray(parsed) && parsed.length > 0) {
        let migrated = false;
        const workflows = parsed.map((raw) => {
          const workflow = parseWorkflowDates(raw);
          if (!workflowNeedsMissionAnchors(workflow)) return workflow;
          migrated = true;
          return normalizeWorkflowMissionGraph(workflow);
        });
        if (migrated) {
          persistWorkflows(workflows);
        }
        return workflows;
      }
    } catch {
      console.warn('Failed to load workflows from storage');
    }
  }

  const starter = createStarterWorkflow();
  persistWorkflows([starter]);
  return [starter];
}
